// Uzbek script conversion: Latin ↔ Cyrillic, plus a plain-ASCII mode for
// passports, e-mail addresses, file names and URL slugs.
//
// Digraphs are matched before single letters, and every apostrophe variant a
// keyboard or a word processor might produce (ʻ ʼ ‘ ’ ` ´ ') is treated as the
// same character, since users paste text from all of them.

export const MODES = ['latinToCyrillic', 'cyrillicToLatin', 'latinToAscii'];

const APOSTROPHES = "'‘’ʻʼ`´ʹ";
const APOSTROPHE_CLASS = `[${APOSTROPHES}]`;
const normalizeApostrophes = (text) => text.replace(new RegExp(APOSTROPHE_CLASS, 'g'), "'");

// Order matters: longer keys are tried first.
const LATIN_TO_CYRILLIC = [
  ["o'", 'ў'], ["g'", 'ғ'],
  ['sh', 'ш'], ['ch', 'ч'], ['ts', 'ц'],
  ['yo', 'ё'], ['yu', 'ю'], ['ya', 'я'], ['ye', 'е'],
  ['a', 'а'], ['b', 'б'], ['d', 'д'], ['e', 'е'], ['f', 'ф'], ['g', 'г'],
  ['h', 'ҳ'], ['i', 'и'], ['j', 'ж'], ['k', 'к'], ['l', 'л'], ['m', 'м'],
  ['n', 'н'], ['o', 'о'], ['p', 'п'], ['q', 'қ'], ['r', 'р'], ['s', 'с'],
  ['t', 'т'], ['u', 'у'], ['v', 'в'], ['x', 'х'], ['y', 'й'], ['z', 'з'],
  ['c', 'к'], ['w', 'в'],
];

const CYRILLIC_TO_LATIN = [
  ['щ', 'shch'], ['ю', 'yu'], ['я', 'ya'], ['ё', 'yo'], ['ч', 'ch'], ['ш', 'sh'],
  ['ц', 'ts'], ['ў', "o'"], ['ғ', "g'"], ['қ', 'q'], ['ҳ', 'h'],
  ['а', 'a'], ['б', 'b'], ['в', 'v'], ['г', 'g'], ['д', 'd'], ['е', 'e'],
  ['ж', 'j'], ['з', 'z'], ['и', 'i'], ['й', 'y'], ['к', 'k'], ['л', 'l'],
  ['м', 'm'], ['н', 'n'], ['о', 'o'], ['п', 'p'], ['р', 'r'], ['с', 's'],
  ['т', 't'], ['у', 'u'], ['ф', 'f'], ['х', 'x'], ['ъ', "'"], ['ь', ''],
  ['э', 'e'], ['ы', 'i'],
];

// Passport/URL friendly spelling. Everything lands inside [a-z0-9].
const LATIN_TO_ASCII = [
  ["o'", 'o'], ["g'", 'g'], ['x', 'kh'], ["'", ''],
];

// Case-insensitive so SALOM and Salom hit the same table entries; applyCase
// puts the casing back afterwards.
const buildPattern = (pairs) => new RegExp(
  pairs.map(([from]) => from.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|'),
  'gi',
);

const isUpper = (chunk) => chunk !== chunk.toLowerCase();
const isAllUpper = (chunk) => chunk.length > 1 && chunk === chunk.toUpperCase()
  && chunk !== chunk.toLowerCase();

// Mirrors the source casing: SALOM → САЛОМ, Salom → Салом, salom → салом.
const applyCase = (source, target) => {
  if (!target) return target;
  if (isAllUpper(source)) return target.toUpperCase();
  if (isUpper(source[0])) return target[0].toUpperCase() + target.slice(1);
  return target;
};

const replaceWith = (text, pairs, transform) => {
  const table = new Map(pairs);
  const pattern = buildPattern(pairs);
  return text.replace(pattern, (match, offset, whole) => {
    const lower = match.toLowerCase();
    const mapped = transform
      ? transform(lower, table.get(lower), offset, whole)
      : table.get(lower);
    return applyCase(match, mapped);
  });
};

const latinToCyrillic = (text) => {
  const source = normalizeApostrophes(text);

  const withVowelRules = replaceWith(source, LATIN_TO_CYRILLIC, (lower, mapped, offset, whole) => {
    const before = whole[offset - 1] ? whole[offset - 1].toLowerCase() : '';
    const atWordStart = !/[a-zA-Zа-яёқғҳў']/.test(before);

    // "e" opens a word as э (Eshik → Эшик) but stays е inside one (kel → кел).
    if (lower === 'e') return atWordStart ? 'э' : 'е';
    return mapped;
  });

  // Tutuq belgisi: the pattern above has no "'" entry, so handle it here where
  // the surrounding letters are already Cyrillic.
  return withVowelRules.replace(new RegExp(APOSTROPHE_CLASS, 'g'), 'ъ');
};

const cyrillicToLatin = (text) => replaceWith(text, CYRILLIC_TO_LATIN, (lower, mapped, offset, whole) => {
  if (lower !== 'е') return mapped;
  // Cyrillic е is "ye" at the start of a word or after a vowel, "e" otherwise.
  const before = whole[offset - 1] ? whole[offset - 1].toLowerCase() : '';
  const atWordStart = !/[a-zа-яёқғҳў]/.test(before);
  return atWordStart || 'аеёиоуўэюя'.includes(before) ? 'ye' : 'e';
});

const latinToAscii = (text) => {
  const source = normalizeApostrophes(text);
  const converted = replaceWith(source, LATIN_TO_ASCII);
  // Strips any leftover accents (é, ü) that came in with pasted text.
  return converted.normalize('NFD').replace(/[̀-ͯ]/g, '');
};

const CONVERTERS = {
  latinToCyrillic,
  cyrillicToLatin,
  latinToAscii,
};

/** Converts `text` using one of MODES; unknown modes return the input unchanged. */
export const transliterate = (text, mode) => {
  if (!text) return '';
  const convert = CONVERTERS[mode];
  return convert ? convert(text) : text;
};

/** URL/file-name slug built on the ASCII spelling: "Toshkent shahri" → "toshkent-shahri". */
export const slugify = (text) => latinToAscii(String(text))
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '');

/** Cheap script sniff so the UI can preselect a sensible direction. */
export const detectScript = (text) => (/[а-яёқғҳўА-ЯЁҚҒҲЎ]/.test(text) ? 'cyrillic' : 'latin');
